// pages/hauler/RouteScreen.js
// Hauler-facing route view.
//
// Renders the Mapbox map (when available) above a numbered stop list.
// If the map fails to load (no token / network / script error), the
// stop list still renders — graceful text fallback per spec.
//
// Stops are the claimed slice of useDriverJobs (claimed / en_route /
// in_progress). Order is creation time. The Hauler can re-order via
// "Sort by earning range" or skip via "Do later" — both are advisory
// reorderings of the local view; neither writes to the DB. Per the
// IC-safe boundary, route order is never compelled by the platform.

import React, { useMemo, useState } from "react";
import { jobCoords } from "../../components/hauler/HaulerMap";
import StatusPill from "../../components/hauler/StatusPill";
import StateConfirmSheet from "../../components/hauler/StateConfirmSheet";
import HaulerToast from "../../components/hauler/HaulerToast";
import { deriveJobEconomics } from "../../hooks/useJobEconomics";
import { getHaulerPayDisclosure, haulerPaySortValue, haulerScopeLabel } from "../../utils/haulerPayDisclosure";

// Hint text for each lifecycle state — drives the card-as-action
// affordance on the Route tab. Returns null for states that do not
// surface an inline action (the card stays informational only).
function hintForStatus(status) {
  if (status === "claimed") return "Tap card when you're heading out";
  if (status === "en_route") return "Tap card when you arrive";
  if (status === "in_progress") return "Tap card to verify and complete";
  return null;
}

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
  amber: "var(--hl-amber)",
};

function navigateUrl(job) {
  const c = jobCoords(job);
  if (c) return `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`;
  if (job?.addr) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.addr)}`;
  return null;
}

export default function RouteScreen({ session, claimed = [], onOpenJob, onAction }) {
  const [order, setOrder] = useState("default");        // 'default' | 'pay'
  const [skippedIds, setSkippedIds] = useState([]);     // 'do later' suppressions
  // Card-as-action state: confirm-sheet target + busy + toast feedback.
  // actionTarget is the stop currently being acted on (null when sheet
  // is closed). Sheet copy + the eventual transition both derive from
  // actionTarget.status.
  const [actionTarget, setActionTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const stops = useMemo(() => {
    const active = claimed.filter(j => !skippedIds.includes(j.id));
    if (order === "pay") {
      return [...active].sort((a, b) => {
        const pa = haulerPaySortValue(a);
        const pb = haulerPaySortValue(b);
        return pb - pa;
      });
    }
    return active;
  }, [claimed, order, skippedIds]);

  const skipped = useMemo(
    () => claimed.filter(j => skippedIds.includes(j.id)),
    [claimed, skippedIds]
  );

  const next = stops[0] || null;
  const remaining = stops.slice(1);

  function doSkip(jobId) {
    setSkippedIds(ids => [...ids, jobId]);
  }
  function doRestore(jobId) {
    setSkippedIds(ids => ids.filter(id => id !== jobId));
  }

  // Open the confirm sheet for a card tap. Only stops with a matching
  // hint (claimed / en_route / in_progress) qualify; other states no-op.
  function doTapAction(stop) {
    if (!hintForStatus(stop?.status)) return;
    setActionTarget(stop);
  }

  // Confirm sheet → fire the transition. claimed → enroute and
  // en_route → arrived call the canonical service paths via onAction
  // (HaulerApp wires that to driverJobService.markEnRoute / markArrived
  // + a refetch). in_progress → completion is delegated to
  // JobDetailScreen so the existing VerificationBlock + CompletionModal
  // flow handles photo capture + tier confirmation; we just navigate.
  async function doConfirmAction() {
    if (!actionTarget || actionBusy) return;
    const { id, status } = actionTarget;
    if (status === "in_progress") {
      // Hand off to JobDetail for the full verification UX.
      setActionTarget(null);
      onOpenJob && onOpenJob(actionTarget);
      return;
    }
    setActionBusy(true);
    try {
      if (status === "claimed") {
        await onAction(actionTarget, "enroute");
        setToast({ message: "Marked on the way. Customer notified.", kind: "ok" });
      } else if (status === "en_route") {
        await onAction(actionTarget, "arrived");
        setToast({ message: "Arrived — job in progress.", kind: "ok" });
      }
      setActionTarget(null);
    } catch (e) {
      setToast({ message: "Couldn't update job status. Try again.", kind: "err" });
    }
    setActionBusy(false);
    void id; // (avoid unused-warning if logging is added later)
  }

  return (
    <div style={{ padding: "20px 16px 96px", color: COLORS.text }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.muted, marginBottom: 4 }}>
            ROUTE
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>Your route</div>
        </div>
        <span style={{ fontSize: 12, color: COLORS.sub }}>
          {stops.length} stop{stops.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Sign-in gate */}
      {!session && (
        <EmptyState title="Sign in required" body="Sign in to see your route." />
      )}

      {session && (
        <>
          {/* No map on the Route tab — zone-first model puts spatial
              discovery on the Jobs tab via JobsMap. The Route tab is
              the execution surface: just the StopCard list with the
              card-as-action lifecycle. */}

          {/* Empty state when no active jobs — sits BELOW the map per spec */}
          {claimed.length === 0 && (
            <EmptyState
              title="No active jobs"
              body="Claim a job from the Jobs tab to start a route."
            />
          )}

          {/* Order controls + stop list only when there's actual route content */}
          {claimed.length > 0 && (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <ToggleBtn
                  label="Default order"
                  active={order === "default"}
                  onClick={() => setOrder("default")}
                />
                <ToggleBtn
                  label="Sort by earning range"
                  active={order === "pay"}
                  onClick={() => setOrder("pay")}
                />
              </div>

              {/* Next stop */}
              {next && (
                <div style={{ marginBottom: 16 }}>
                  <SectionHeader label="Next stop" />
                  <StopCard
                    stop={next}
                    index={1}
                    accent
                    onOpenDetail={onOpenJob}
                    onTapAction={doTapAction}
                    onSkip={() => doSkip(next.id)}
                  />
                </div>
              )}

              {/* Remaining */}
              {remaining.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <SectionHeader label={`Remaining · ${remaining.length}`} />
                  {remaining.map((stop, idx) => (
                    <StopCard
                      key={stop.id}
                      stop={stop}
                      index={idx + 2}
                      onOpenDetail={onOpenJob}
                      onTapAction={doTapAction}
                      onSkip={() => doSkip(stop.id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Skipped (advisory; user can restore at any time) */}
          {skipped.length > 0 && (
            <div>
              <SectionHeader label={`Doing later · ${skipped.length}`} />
              {skipped.map((stop) => (
                <SkippedRow key={stop.id} stop={stop} onRestore={() => doRestore(stop.id)} onOpen={onOpenJob} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Card-as-action confirm sheet (state-aware copy from the
          target stop's status). Mounts as a fixed overlay; the
          underlying Route content remains scrollable behind it. */}
      {actionTarget && (
        <StateConfirmSheet
          status={actionTarget.status}
          busy={actionBusy}
          onCancel={() => { if (!actionBusy) setActionTarget(null); }}
          onConfirm={doConfirmAction}
        />
      )}

      {/* Toast feedback for transition results. Same component the
          JobDetailScreen uses for consistent visual treatment. */}
      <HaulerToast
        message={toast?.message}
        kind={toast?.kind}
        onDismiss={() => setToast(null)}
      />
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{
      fontSize: 11, letterSpacing: 1.5, fontWeight: 700,
      color: COLORS.muted, textTransform: "uppercase",
      margin: "4px 2px 8px",
    }}>
      {label}
    </div>
  );
}

function StopCard({ stop, index, accent, onOpenDetail, onTapAction, onSkip }) {
  const econ = deriveJobEconomics(stop);
  const disclosure = getHaulerPayDisclosure(stop, econ);
  const navUrl = navigateUrl(stop);
  const hasCoords = !!jobCoords(stop);
  const hint = hintForStatus(stop.status);
  // The card body is the lifecycle action surface. If there's no
  // hint for this status, the card is informational only and tap
  // does nothing (the chevron still opens detail).
  const isActionable = !!hint && !!onTapAction;

  function handleCardTap() {
    if (isActionable) onTapAction(stop);
  }
  function handleCardKey(e) {
    if (!isActionable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onTapAction(stop);
    }
  }
  function handleChevron(e) {
    e.stopPropagation();
    onOpenDetail && onOpenDetail(stop);
  }
  function handleButtonRow(e) {
    // Stop the card-tap from firing when the user presses Navigate
    // or Do later. Both buttons handle their own onClick; this just
    // prevents bubble-up to the card root.
    e.stopPropagation();
  }

  return (
    <div
      onClick={handleCardTap}
      onKeyDown={handleCardKey}
      role={isActionable ? "button" : undefined}
      tabIndex={isActionable ? 0 : undefined}
      aria-label={isActionable ? `${stop.addr || "Job"} — ${hint}` : undefined}
      style={{
        background: COLORS.card,
        border: `1px solid ${accent ? COLORS.sky : COLORS.border}`,
        borderRadius: 14,
        padding: "14px 14px 12px",
        marginBottom: 10,
        cursor: isActionable ? "pointer" : "default",
      }}
    >
      {/* Top row: pin + status + zone + chevron-to-detail */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: hint ? 6 : 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={pinStyle}>{index}</span>
          <StatusPill status={stop.status} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: COLORS.muted }}>Zone {stop.zone || "—"}</span>
          <button
            type="button"
            onClick={handleChevron}
            aria-label="View job details"
            style={chevronBtnStyle}
          >
            ›
          </button>
        </div>
      </div>

      {/* Hint row — drives the card-as-action affordance */}
      {hint && (
        <div style={{
          fontSize: 12,
          color: COLORS.sky,
          fontWeight: 600,
          marginBottom: 10,
          letterSpacing: 0.1,
        }}>
          {hint}
        </div>
      )}

      {/* Address + scope — pre-verification hides deterministic tier labels */}
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
        {stop.addr || "Address pending"}
      </div>
      <div style={{ fontSize: 12, color: COLORS.sub, marginBottom: 12 }}>
        {disclosure.showTierLabel ? (econ?.tierLabel || "—") : haulerScopeLabel(stop)} · {disclosure.payText}
      </div>

      {/* Secondary actions — Navigate (external) + Do later (skip).
          Wrapper stops propagation so a tap here does NOT fire the
          card-level lifecycle action. */}
      <div style={{ display: "flex", gap: 8 }} onClick={handleButtonRow}>
        {navUrl ? (
          <a
            href={navUrl}
            target="_blank"
            rel="noreferrer"
            style={{ ...secondaryBtn, textDecoration: "none" }}
          >
            {hasCoords ? "Navigate" : "Address only"}
          </a>
        ) : (
          <button disabled style={{ ...secondaryBtn, opacity: 0.5, cursor: "not-allowed" }}>
            No address
          </button>
        )}
        <button onClick={onSkip} style={secondaryBtn}>Do later</button>
      </div>
    </div>
  );
}

function SkippedRow({ stop, onRestore, onOpen }) {
  return (
    <div style={{
      background: "var(--hl-input-bg)",
      border: `1px dashed ${COLORS.border}`,
      borderRadius: 12,
      padding: "10px 12px",
      marginBottom: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}>
      <div onClick={() => onOpen && onOpen(stop)} style={{ cursor: "pointer", flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {stop.addr || "Address pending"}
        </div>
        <div style={{ fontSize: 11, color: COLORS.sub }}>Zone {stop.zone || "—"}</div>
      </div>
      <button onClick={onRestore} style={{
        ...secondaryBtn,
        flex: "0 0 auto",
        padding: "6px 12px",
        minHeight: 36,
        fontSize: 11,
      }}>
        Restore
      </button>
    </div>
  );
}

function ToggleBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "8px 10px",
        borderRadius: 999,
        border: `1px solid ${active ? COLORS.sky : COLORS.border}`,
        background: active ? "rgba(61,144,217,.14)" : "transparent",
        color: active ? COLORS.sky : COLORS.sub,
        fontFamily: "inherit",
        fontSize: 12,
        fontWeight: active ? 800 : 600,
        cursor: "pointer",
        minHeight: 44,
      }}
    >
      {label}
    </button>
  );
}

function EmptyState({ title, body }) {
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: "20px 18px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

const pinStyle = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  background: COLORS.sky,
  color: "#FFFFFF",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 800,
};

// Chevron-to-detail button. Sits in the top-right of the StopCard so
// the user can open JobDetail without firing the card-level lifecycle
// action. Visually unobtrusive — the action affordance lives on the
// card body itself per Bejorn's card-as-action spec.
const chevronBtnStyle = {
  width: 32,
  height: 32,
  minWidth: 32,
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  background: "transparent",
  color: COLORS.sub,
  fontFamily: "inherit",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const secondaryBtn = {
  flex: 1,
  // inline-flex + center alignment makes the <a> and <button> variants
  // render text identically. <a> defaults to display:inline (text rides
  // the top of the padding box); <button> defaults to display:inline-block
  // (text centers in the box). Forcing both to inline-flex with centered
  // axes erases that browser-default divergence.
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "var(--hl-input-bg)",
  color: COLORS.text,
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
  cursor: "pointer",
  minHeight: 44,
};
